<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="generatorFields"
    :hasGenerator="hasGenerator"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
    @disableChangingInformation="$emit('disableChangingInformation')"
  />
</template>

<script setup lang="ts">
import { useGeneratorFinder } from "@/composables/useGeneratorFinder";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { Account } from "@/types";
import { useOptionalContractInclusion } from "@/signup/composables/useOptionalContractInclusion";
import { reactive, computed } from "vue";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import { useI18n } from "vue-i18n";
import {
  LV_AUTHORIZATION_APPLICATION,
  LV_AST_TERMS,
  LV_SOLDERA_MEMBERSHIP,
  LV_ACCOUNT_HOLDER_REGISTRATION,
  LV_ACCOUNT_HOLDER_REGISTRATION_AUTH,
  LV_PRODUCTION_DEVICE_REGISTRATION,
  LV_GRID_CONNECTION_SCHEME,
  LV_DEVICE_AUXILIARIES,
  LV_POWER_OF_ATTORNEY,
} from "@/signup/contract_keys";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    addNewDevice?: boolean;
  }>(),
  { addNewDevice: false }
);

defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);
const companyFields = reactive([
  { label: t("company.name"), value: selectedCompany.value.name },
  {
    label: t("company.registry_code"),
    value: selectedCompany.value.registry_code,
  },
  { label: t("company.vat"), value: selectedCompany.value.vat },
  { label: t("company.address"), value: selectedCompany.value.address },
  {
    label: t("company.bank_account"),
    value: selectedCompany.value.bank_account,
  },
  { label: t("company.bank_name"), value: selectedCompany.value.bank_name },
  {
    label: t("company.bank_swift"),
    value: selectedCompany.value.bank_swift,
  },
]);
const { nullableSelectedGenerator } = useGeneratorFinder(props.accountData);
const hasGenerator = computed(() => {
  return nullableSelectedGenerator.value.generator !== null;
});
const yesNo = (value: boolean | null | undefined) =>
  value == null ? value : t(value ? "common.yes" : "common.no");

const generator = nullableSelectedGenerator.value.generator;
const generatorFields = generator
  ? [
      { title: "device_name", value: generator.device_name },
      { title: "device_technology", value: generator.device_technology },
      { title: "device_power", value: generator.device_power },
      {
        title: "device_power_in_permit",
        value: generator.device_power_in_permit,
      },
      { title: "device_eic", value: generator.device_eic },
      {
        title: "only_device_output",
        value: yesNo(generator.only_device_output),
      },
      { title: "device_address", value: generator.device_address },
      { title: "lat", value: generator.lat },
      { title: "lng", value: generator.lng },
      { title: "device_grid_operator", value: generator.device_grid_operator },
      {
        title: "grid_contract_start_date",
        value: generator.grid_contract_start_date,
      },
    ].map(({ title, value }) => ({
      label: t(`signup.lv.generator_review.${title}.title`),
      value,
    }))
  : [];

const contractState = computed(() => {
  if (props.addNewDevice) {
    return 2;
  }
  return 1;
});

const { includeDocument } = useOptionalContractInclusion(
  selectedCompany,
  contractState
);

// Build base contracts list
const baseContracts = [
  {
    id: 1,
    key: LV_AUTHORIZATION_APPLICATION,
    contract_title: t(
      "signup.contract_review_component.lv.lv_account_authorization_form_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_account_authorization_form_text"
    ),
    include: includeDocument([1, 2], "lv_account_authorization_form").value,
    can_include: true,
  },
  {
    id: 2,
    key: LV_AST_TERMS,
    contract_title: t(
      "signup.contract_review_component.lv.lv_ast_terms_and_service_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_ast_terms_and_service_text"
    ),
    include: includeDocument([2], LV_AST_TERMS).value,
    can_include: true,
  },
  {
    id: 3,
    key: LV_SOLDERA_MEMBERSHIP,
    contract_title: t(
      "signup.contract_review_component.lv.lv_soldera_membership_agreement_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_soldera_membership_agreement_text"
    ),
    include: includeDocument([1, 2], LV_SOLDERA_MEMBERSHIP).value,
    can_include: true,
  },
  {
    id: 4,
    key: LV_ACCOUNT_HOLDER_REGISTRATION,
    contract_title: t(
      "signup.contract_review_component.lv.lv_soldera_account_holder_registration_form_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_soldera_account_holder_registration_form_text"
    ),
    include: includeDocument([2], LV_ACCOUNT_HOLDER_REGISTRATION).value,
    can_include: true,
  },
  {
    id: 5,
    key: LV_ACCOUNT_HOLDER_REGISTRATION_AUTH,
    contract_title: t(
      "signup.contract_review_component.lv.lv_soldera_account_holder_registration_form_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_soldera_account_holder_registration_form_text"
    ),
    include: includeDocument([1], LV_ACCOUNT_HOLDER_REGISTRATION_AUTH).value,
    can_include: true,
  },
  {
    id: 5,
    key: LV_PRODUCTION_DEVICE_REGISTRATION,
    contract_title: t(
      "signup.contract_review_component.lv.lv_new_device_application_title"
    ),
    contract_description: t(
      "signup.contract_review_component.lv.lv_new_device_application_text"
    ),
    include: includeDocument([2], LV_PRODUCTION_DEVICE_REGISTRATION).value,
    can_include: true,
  },
  {
    id: 6,
    key: LV_GRID_CONNECTION_SCHEME,
    contract_title: t("signup.multiple_upload.UF_lv_grid_connection_scheme"),
    contract_description: t(
      "signup.multiple_upload.UF_lv_grid_connection_scheme_where_to_find"
    ),
    include: includeDocument([2], LV_GRID_CONNECTION_SCHEME).value,
    can_include: true,
  },
];

// Add optional document entries only if they exist in generator.files
const optionalDocuments = [];

if (nullableSelectedGenerator.value.generator?.files?.[LV_DEVICE_AUXILIARIES]) {
  optionalDocuments.push({
    id: 7,
    key: LV_DEVICE_AUXILIARIES,
    contract_title: t(
      "signup.multiple_upload.UF_lv_document_of_device_auxiliaries"
    ),
    contract_description: t(
      "signup.multiple_upload.UF_lv_document_of_device_auxiliaries_where_to_find"
    ),
    include: includeDocument([2], LV_DEVICE_AUXILIARIES).value,
    can_include: true,
  });
}

if (nullableSelectedGenerator.value.generator?.files?.[LV_POWER_OF_ATTORNEY]) {
  optionalDocuments.push({
    id: 8,
    key: LV_POWER_OF_ATTORNEY,
    contract_title: t("signup.multiple_upload.UF_lv_power_of_attorney"),
    contract_description: t(
      "signup.multiple_upload.UF_lv_power_of_attorney_where_to_find"
    ),
    include: includeDocument([2], LV_POWER_OF_ATTORNEY).value,
    can_include: true,
  });
}

// Combine base contracts and optional documents
const contractsList = [...baseContracts, ...optionalDocuments];

// https://drive.google.com/drive/u/0/folders/1SX235_DEzJSBvmeJssPaWGSk2o_MyNfY
const contracts = reactive([[...contractsList]]);
</script>

<style scoped lang="scss"></style>
